import { create } from 'zustand';
import { api, ApiError } from '@/utils/api';

interface User {
  id: number;
  email: string;
  display_name: string;
  avatar_url: string | null;
  is_active: number;
  is_admin: number;
  source: string;
  locale: string;
  theme: string;
}

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
  fetchMe: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName: string) => Promise<void>;
  logout: () => Promise<void>;
  forgotPassword: (email: string) => Promise<string>;
  resetPassword: (token: string, password: string) => Promise<string>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  loading: true,
  error: null,

  fetchMe: async () => {
    try {
      const data = await api.get<{ user: User }>('/auth/me');
      set({ user: data.user, loading: false });
    } catch {
      set({ user: null, loading: false });
    }
  },

  login: async (email, password) => {
    set({ error: null });
    try {
      const data = await api.post<{ user: User }>('/auth/login', { email, password });
      set({ user: data.user });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Login fehlgeschlagen';
      set({ error: msg });
      throw err;
    }
  },

  register: async (email, password, displayName) => {
    set({ error: null });
    try {
      const data = await api.post<{ user: User }>('/auth/register', { email, password, display_name: displayName });
      set({ user: data.user });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Registrierung fehlgeschlagen';
      set({ error: msg });
      throw err;
    }
  },

  logout: async () => {
    await api.post('/auth/logout');
    set({ user: null });
  },

  forgotPassword: async (email) => {
    const data = await api.post<{ message: string }>('/auth/forgot-password', { email });
    return data.message;
  },

  resetPassword: async (token, password) => {
    const data = await api.post<{ message: string }>('/auth/reset-password', { token, password });
    return data.message;
  },

  clearError: () => set({ error: null }),
}));
